import React from "react";
import { Button } from "@/components/ui/button";
import { CalendarPlusIcon as CalendarPlus, ArrowsClockwiseIcon as ArrowsClockwise } from "@phosphor-icons/react";

function fmtDateYMD(d) {
  return d.toISOString().slice(0, 10).replace(/-/g, "");
}

/** Customer-side banner on a completed booking with a groomer-suggested
 * rebook date: one-tap rebook + add-to-calendar (Google / Apple .ics). */
export default function RebookBanner({ b }) {
  const due = new Date(b.rebook_due_at);
  const title = `Next groom with ${b.provider_business || "your groomer"}`;
  const dayAfter = new Date(due.getTime() + 24 * 3600 * 1000);
  const googleHref =
    `https://calendar.google.com/calendar/render?action=TEMPLATE` +
    `&text=${encodeURIComponent(title)}` +
    `&dates=${fmtDateYMD(due)}/${fmtDateYMD(dayAfter)}` +
    `&details=${encodeURIComponent("Suggested by your groomer on Armstrong K9 Registry. Rebook: https://armstrongk9registry.com" + (b.provider_slug ? `/provider/${b.provider_slug}` : "/find-pros"))}`;

  const downloadIcs = () => {
    const ics = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Armstrong K9 Registry//EN",
      "BEGIN:VEVENT",
      `UID:rebook-${b.id}@armstrongk9registry.com`,
      `DTSTART;VALUE=DATE:${fmtDateYMD(due)}`,
      `DTEND;VALUE=DATE:${fmtDateYMD(dayAfter)}`,
      `SUMMARY:${title}`,
      "DESCRIPTION:Suggested by your groomer on Armstrong K9 Registry.",
      "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "next-groom.ics";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#F0F0F0] flex items-center justify-between gap-3 flex-wrap" data-testid={`rebook-banner-${b.id}`}>
      <p className="text-[12.5px] text-[#3a3a3a]">
        <span className="font-semibold text-ink">✂️ Next groom suggested:</span>{" "}
        {due.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
      </p>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" className="rounded-full bg-ink hover:bg-crimson text-white"
          onClick={() => (window.location.href = b.provider_slug ? `/provider/${b.provider_slug}` : "/find-pros")}
          data-testid={`rebook-now-${b.id}`}>
          <ArrowsClockwise size={13} weight="duotone" className="mr-1" /> Rebook
        </Button>
        <a href={googleHref} target="_blank" rel="noreferrer">
          <Button size="sm" variant="outline" className="rounded-full" data-testid={`rebook-gcal-${b.id}`}>
            <CalendarPlus size={13} weight="duotone" className="mr-1" /> Google Cal
          </Button>
        </a>
        <Button size="sm" variant="outline" className="rounded-full" onClick={downloadIcs} data-testid={`rebook-ics-${b.id}`}>
          <CalendarPlus size={13} weight="duotone" className="mr-1" /> Apple / .ics
        </Button>
      </div>
    </div>
  );
}
